import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAppSelector } from "@/app/store";
import {
  selectUser,
  selectUserPermissions,
} from "@/app/store/slices/auth.slice";
import {
  canAccessCkPreparedItemRoutes,
  canAccessRequestsSentRoutes,
  isBranchAccountUser,
} from "@/shared/constants";

type PreparedItemRequestAudience = "kitchen" | "branch";

interface PreparedItemRequestAudienceGuardProps {
  audience: PreparedItemRequestAudience;
  children: ReactNode;
}

/**
 * Incoming Requests routes are kitchen-only; Requests Sent routes are branch-only.
 * Matches sidebar visibility so a typed URL cannot open the other side.
 */
export function PreparedItemRequestAudienceGuard({
  audience,
  children,
}: PreparedItemRequestAudienceGuardProps) {
  const user = useAppSelector(selectUser);
  const permissions = useAppSelector(selectUserPermissions);
  const isBranchUser = isBranchAccountUser(user);
  const canAccessKitchen = canAccessCkPreparedItemRoutes(
    permissions,
    isBranchUser,
  );
  const canAccessBranch = canAccessRequestsSentRoutes(
    permissions,
    isBranchUser,
  );

  if (audience === "kitchen") {
    if (canAccessKitchen) {
      return <>{children}</>;
    }

    return (
      <Navigate
        to={canAccessBranch ? "/requests-sent" : "/unauthorized"}
        replace
      />
    );
  }

  if (canAccessBranch) {
    return <>{children}</>;
  }

  return (
    <Navigate
      to={canAccessKitchen ? "/prepared-item-requests" : "/unauthorized"}
      replace
    />
  );
}
